import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { AppSidebar } from '@/components/app-sidebar'
import { SiteHeader } from '@/components/site-header'
import { ThemeProvider } from '@/components/theme-provider'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { Toaster } from '@/components/ui/sonner'
import { AuthProvider } from '@/contexts/AuthContext'
import { SavedViewsProvider } from '@/contexts/SavedViewsContext'
import { CustomDashboardProvider } from '@/contexts/CustomDashboardContext'
import { SaveViewModal, SavedViewsSidebar } from '@/components/SavedViews'
import { ProtectedRoute, PublicOnlyRoute, RoleProtectedRoute } from '@/components/ProtectedRoute'
import { DesktopOnlyGuard } from '@/components/DesktopOnlyGuard'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

import {
  OverviewPage,
  BreakdownPage,
  CustomDashboardPage,
  CustomDashboardViewPage,
  ReportsPage,
  AlertsPage,
  ApiKeysPage,
  TeamPage,
  SettingsPage,
  BillingPage,
  CheckoutSuccessPage,
} from '@/pages'

// Auth Pages
import LoginPage from '@/components/shadcn-studio/blocks/login-page-01/login-page-01'
import RegisterPage from '@/components/shadcn-studio/blocks/register-01/register-01'
import EmailConfirmationPage from '@/pages/auth/EmailConfirmationPage'
import CompleteRegistrationPage from '@/pages/auth/CompleteRegistrationPage'
import AcceptInvitationPage from '@/pages/auth/AcceptInvitationPage'
import OrganizationSetupPage from '@/pages/auth/OrganizationSetupPage'
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage'
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage'
import NotFoundPage from '@/pages/NotFoundPage'
import { ActivityPage } from './pages/CostExplorer/Activity'

function DashboardLayout({ children }: { children: React.ReactNode }) {
  return (
    <SavedViewsProvider>
      <CustomDashboardProvider>
        <SidebarProvider
          style={
            {
              '--sidebar-width': 'calc(var(--spacing) * 65)',
              '--header-height': '45px',
            } as React.CSSProperties
          }
        >
          <AppSidebar variant='inset' />
          <SidebarInset>
            <SiteHeader />
            <div className='flex flex-1 flex-col'>
              <div className='@container/main flex flex-1 flex-col gap-2'>{children}</div>
            </div>
          </SidebarInset>
          <SaveViewModal />
          <SavedViewsSidebar />
        </SidebarProvider>
      </CustomDashboardProvider>
    </SavedViewsProvider>
  )
}

export default function App() {
  return (
    <DesktopOnlyGuard>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ThemeProvider>
            <AuthProvider>
              <Routes>
                {/* Public Auth Routes */}
                <Route
                  path='/auth/login'
                  element={
                    <PublicOnlyRoute>
                      <LoginPage />
                    </PublicOnlyRoute>
                  }
                />
                <Route
                  path='/auth/register'
                  element={
                    <PublicOnlyRoute>
                      <RegisterPage />
                    </PublicOnlyRoute>
                  }
                />
                <Route
                  path='/auth/email-confirmation'
                  element={<EmailConfirmationPage />}
                />
                <Route
                  path='/auth/complete-registration'
                  element={
                    <PublicOnlyRoute>
                      <CompleteRegistrationPage />
                    </PublicOnlyRoute>
                  }
                />
                <Route
                  path='/invite/accept'
                  element={
                    <PublicOnlyRoute>
                      <AcceptInvitationPage />
                    </PublicOnlyRoute>
                  }
                />
                <Route
                  path='/auth/organization-setup'
                  element={
                    <ProtectedRoute>
                      <OrganizationSetupPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/auth/forgot-password'
                  element={
                    <PublicOnlyRoute>
                      <ForgotPasswordPage />
                    </PublicOnlyRoute>
                  }
                />
                <Route
                  path='/auth/reset-password'
                  element={<ResetPasswordPage />}
                />

                {/* Protected Dashboard Routes */}
                <Route
                  path='/'
                  element={
                    <ProtectedRoute>
                      <DashboardLayout>
                        <Navigate to='/overview' replace />
                      </DashboardLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/overview'
                  element={
                    <ProtectedRoute>
                      <DashboardLayout>
                        <OverviewPage />
                      </DashboardLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/breakdown'
                  element={
                    <ProtectedRoute>
                      <DashboardLayout>
                        <BreakdownPage />
                      </DashboardLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/activity'
                  element={
                    <ProtectedRoute>
                      <DashboardLayout>
                        <ActivityPage />
                      </DashboardLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/custom-dashboards'
                  element={
                    <ProtectedRoute>
                      <DashboardLayout>
                        <CustomDashboardPage />
                      </DashboardLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/custom-dashboards/:id'
                  element={
                    <ProtectedRoute>
                      <DashboardLayout>
                        <CustomDashboardViewPage />
                      </DashboardLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path='/reports'
                  element={
                    <ProtectedRoute>
                      <DashboardLayout>
                        <ReportsPage />
                      </DashboardLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/alerts'
                  element={
                    <ProtectedRoute>
                      <DashboardLayout>
                        <AlertsPage />
                      </DashboardLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/organization/api-keys'
                  element={
                    <RoleProtectedRoute>
                      <DashboardLayout>
                        <ApiKeysPage />
                      </DashboardLayout>
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path='/organization/team'
                  element={
                    <RoleProtectedRoute>
                      <DashboardLayout>
                        <TeamPage />
                      </DashboardLayout>
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path='/organization/settings'
                  element={
                    <RoleProtectedRoute>
                      <DashboardLayout>
                        <SettingsPage />
                      </DashboardLayout>
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path='/organization/billing'
                  element={
                    <RoleProtectedRoute>
                      <DashboardLayout>
                        <BillingPage />
                      </DashboardLayout>
                    </RoleProtectedRoute>
                  }
                />
                <Route
                  path='/organization/billing/checkout-success'
                  element={
                    <RoleProtectedRoute>
                      <DashboardLayout>
                        <CheckoutSuccessPage />
                      </DashboardLayout>
                    </RoleProtectedRoute>
                  }
                />

                {/* 404 Not Found */}
                <Route path='*' element={<NotFoundPage />} />
              </Routes>
              <Toaster />
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </DesktopOnlyGuard>
  )
}
